import { useCallback, useMemo, useState } from "react";
import { UUID } from "crypto";
import {
  useClientGroups as useClientGroupsV2,
  useClientGroup as useClientGroupV2,
  useClientGroupMutations,
  usePartners as usePartnersV2,
} from "@stockline/web";
import type {
  ClientGroup,
  ClientGroupCreate,
  ClientGroupUpdate,
  ClientGroupWithPartnerCount,
} from "@/app/api/types/client_group";

/**
 * Convert a V2 (camelCase) ClientGroup row into the legacy snake_case
 * `ClientGroup` shape consumed by the frontend.
 */
function v2GroupToLegacy(g: {
  id: string;
  companyId: string;
  name: string;
  color: string | null;
  description: string | null;
  aiContext: string | null;
  createdAt: string;
  updatedAt: string;
}): ClientGroup {
  return {
    id: g.id,
    company_id: g.companyId,
    name: g.name,
    color: g.color,
    description: g.description,
    ai_context: g.aiContext,
    created_at: g.createdAt,
    updated_at: g.updatedAt,
  };
}

/**
 * Get all client groups for the current company, with derived partner counts.
 *
 * Reads flow through the offline-first `client-groups` Dexie store
 * (`useClientGroups` from `@stockline/web`); partner counts are computed
 * locally by joining against `partner.clientGroupIds`. No REST GET fires —
 * the SyncEngine performs the cold-load on `AISyncProvider` mount.
 *
 * Wrapped in TanStack's reactive query shape (`{ data, isLoading, ... }`)
 * for backwards compatibility with the existing call sites in
 * `/client-groups`, `/partners`, recap filters, and pricing pages.
 */
export function useClientGroups() {
  const v2Groups = useClientGroupsV2();
  const v2Partners = usePartnersV2();

  const data = useMemo<ClientGroupWithPartnerCount[]>(() => {
    // Bucket partner counts by group id (linear pass over partners).
    const counts = new Map<string, number>();
    for (const p of v2Partners) {
      if (p.deletedAt) continue;
      // Defend against pre-v29 Dexie records whose row predates the
      // `clientGroupIds` field — `?? []` so the iteration stays a no-op
      // until SSE / full pull refreshes the row.
      for (const id of p.clientGroupIds ?? []) {
        counts.set(id, (counts.get(id) ?? 0) + 1);
      }
    }
    return v2Groups
      .filter((g) => !g.deletedAt)
      .map((g) => ({
        ...v2GroupToLegacy(g),
        partner_count: counts.get(g.id) ?? 0,
      }));
  }, [v2Groups, v2Partners]);

  return {
    data,
    isLoading: false,
    isError: false,
    error: null as Error | null,
    isSuccess: true,
    refetch: async () => {},
  };
}

/**
 * Get a single client group by ID — offline-first.
 */
export function useClientGroup(id: UUID | null) {
  const v2 = useClientGroupV2(id as string | null);

  const data = useMemo<ClientGroup | undefined>(
    () => (v2 ? v2GroupToLegacy(v2) : undefined),
    [v2],
  );

  return {
    data,
    isLoading: false,
    isError: false,
    error: null as Error | null,
    isSuccess: !!v2,
    refetch: async () => {},
  };
}

/**
 * Get all client groups for a specific partner — offline-first.
 *
 * Joins `partner.clientGroupIds` against the offline `client-groups` store.
 */
export function usePartnerGroups(partnerId: UUID | null) {
  const v2Groups = useClientGroupsV2();
  const v2Partners = usePartnersV2();

  const data = useMemo<ClientGroup[]>(() => {
    if (!partnerId) return [];
    const partner = v2Partners.find((p) => p.id === (partnerId as string));
    if (!partner) return [];
    const ids = new Set(partner.clientGroupIds ?? []);
    return v2Groups
      .filter((g) => !g.deletedAt && ids.has(g.id))
      .map((g) => v2GroupToLegacy(g));
  }, [partnerId, v2Groups, v2Partners]);

  return {
    data,
    isLoading: false,
    isError: false,
    error: null as Error | null,
    isSuccess: true,
    refetch: async () => {},
  };
}

/**
 * Adapter that wraps a V2 mutation function into TanStack's
 * `{ mutate, mutateAsync, isPending, error, reset }` shape so the existing
 * ~30 callsites in `/client-groups`, `/partners`, recap, and pricing pages
 * don't need to change. Consumers using `mutateAsync` continue to work; the
 * underlying call now goes through the offline-first SyncEngine instead of
 * a REST round-trip.
 */
function useV2MutationAdapter<TVariables, TResult>(
  fn: (vars: TVariables) => Promise<TResult>,
) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = useCallback(
    async (vars: TVariables): Promise<TResult> => {
      setIsPending(true);
      setError(null);
      try {
        const result = await fn(vars);
        return result;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    [fn],
  );

  /**
   * TanStack-style `mutate(vars, { onSuccess, onError })` — the legacy
   * callers in PartnerRow / ClientGroupForm / recap pages use this exact
   * shape (positional second arg with callback options), so we mirror it
   * verbatim. `onSettled` runs after both branches.
   *
   * Callbacks are wrapped in try/catch — match TanStack Query's behavior of
   * swallowing callback errors so a buggy `onSuccess` never surfaces as an
   * unhandled promise rejection (we don't have a `.catch` downstream of the
   * `.then(success, error)` two-arg form, by design).
   */
  const mutate = useCallback(
    (
      vars: TVariables,
      options?: {
        onSuccess?: (data: TResult) => void;
        onError?: (err: Error) => void;
        onSettled?: (data: TResult | null, err: Error | null) => void;
      },
    ) => {
      mutateAsync(vars).then(
        (data) => {
          try {
            options?.onSuccess?.(data);
          } catch {
            // swallow — match TanStack Query semantics
          }
          try {
            options?.onSettled?.(data, null);
          } catch {
            // swallow
          }
        },
        (e: unknown) => {
          const err = e instanceof Error ? e : new Error(String(e));
          try {
            options?.onError?.(err);
          } catch {
            // swallow
          }
          try {
            options?.onSettled?.(null, err);
          } catch {
            // swallow
          }
        },
      );
    },
    [mutateAsync],
  );

  const reset = useCallback(() => {
    setIsPending(false);
    setError(null);
  }, []);

  return { mutate, mutateAsync, isPending, error, reset };
}

/**
 * Create a new client group via the V2 push-batch path. Optimistically
 * writes to the offline `client-groups` Dexie store and enqueues a
 * `client-group.create` change; the SSE replay confirms with the
 * server-stamped syncId on success.
 */
export function useCreateClientGroup() {
  const { createClientGroup } = useClientGroupMutations();

  return useV2MutationAdapter(async (data: ClientGroupCreate) => {
    return createClientGroup({
      name: data.name,
      color: data.color ?? null,
      description: data.description ?? null,
      aiContext: data.ai_context ?? null,
    });
  });
}

/**
 * Update a client group via V2 push-batch.
 *
 * `ClientGroupUpdate` uses the legacy `UndefinedType` (`{}`) marker for
 * "explicitly unset" — at this layer we collapse it into a plain object
 * spread so the V2 hook only sees real values. `as unknown as ...`
 * coercions are needed because the legacy `string | UndefinedType` union
 * isn't structurally assignable to V2's `string | undefined`.
 */
export function useUpdateClientGroup() {
  const { updateClientGroup } = useClientGroupMutations();

  return useV2MutationAdapter(
    async ({ id, data }: { id: UUID; data: ClientGroupUpdate }) => {
      const v2Data: Record<string, unknown> = {};
      if (data.name !== undefined) v2Data.name = data.name;
      if (data.color !== undefined) v2Data.color = data.color;
      if (data.description !== undefined) v2Data.description = data.description;
      if (data.ai_context !== undefined) v2Data.aiContext = data.ai_context;
      await updateClientGroup(
        id as string,
        v2Data as Parameters<typeof updateClientGroup>[1],
      );
    },
  );
}

/**
 * Delete a client group via V2 push-batch. The server soft-deletes
 * (sets `deleted_at`) and publishes `client-group.deleted`.
 */
export function useDeleteClientGroup() {
  const { deleteClientGroup } = useClientGroupMutations();

  return useV2MutationAdapter(async (id: UUID) => {
    await deleteClientGroup(id as string);
  });
}

/**
 * Add a partner to a client group via the partner push-batch path. Reads
 * the partner's current memberships from Dexie, appends the group id, and
 * pushes the full re-set as a `partner.update` payload — the server runs
 * an atomic delete + insert against `partner_client_groups` in the same
 * transaction as the partner UPDATE.
 */
export function useAddPartnerToGroup() {
  const { addPartnerToGroup } = useClientGroupMutations();

  return useV2MutationAdapter(
    async ({ partnerId, groupId }: { partnerId: UUID; groupId: UUID }) => {
      await addPartnerToGroup(partnerId as string, groupId as string);
    },
  );
}

/**
 * Remove a partner from a client group via the partner push-batch path.
 */
export function useRemovePartnerFromGroup() {
  const { removePartnerFromGroup } = useClientGroupMutations();

  return useV2MutationAdapter(
    async ({ partnerId, groupId }: { partnerId: UUID; groupId: UUID }) => {
      await removePartnerFromGroup(partnerId as string, groupId as string);
    },
  );
}
